import { useEffect, useState } from "react";
import axios from "axios";
import { DollarSign, Loader2, Save, CheckCircle2 } from "lucide-react";
import { API_URL } from "@/lib/links";
import { money, savingsLabel } from "@/lib/usePricing";

const rows = [
  { id: "monthly", label: "Monthly", period: "/ month" },
  { id: "yearly", label: "Yearly", period: "/ year" },
];

const NumberField = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-muted">{label}</span>
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted">$</span>
      <input type="number" min="0" step="0.01" inputMode="decimal" className="field !pl-7 text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  </label>
);

const PlanRow = ({ row, value, onChange }) => {
  const parsed = { price: Number(value.price) || 0, others: Number(value.others) || 0 };
  const save = savingsLabel(row.id, parsed);
  return (
    <div className="rounded-xl border border-line bg-panel2/60 p-4" data-testid={`pricing-row-${row.id}`}>
      <p className="text-sm font-medium text-body">{row.label}</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <NumberField label="Your price" value={value.price} onChange={(price) => onChange({ ...value, price })} testId={`pricing-price-input-${row.id}`} />
        <NumberField label="Competitors charge" value={value.others} onChange={(others) => onChange({ ...value, others })} testId={`pricing-others-input-${row.id}`} />
      </div>
      <p className="mt-3 text-xs text-muted" data-testid={`pricing-preview-${row.id}`}>
        Shows as <span className="font-semibold text-silver">{money(parsed.price)}</span> {row.period}
        {save && <> · <span className="text-emerald-300">{save}</span></>}
      </p>
    </div>
  );
};

export const PricingPanel = ({ headers }) => {
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API_URL}/pricing`).then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = Object.fromEntries(rows.map((r) => [r.id, { price: Number(pricing[r.id].price), others: Number(pricing[r.id].others) }]));
      const { data } = await axios.put(`${API_URL}/admin/pricing`, payload, { headers });
      setPricing(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setError(err.response?.data?.detail?.[0]?.msg || "Could not save prices. Check the numbers and try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!pricing) return null;
  return (
    <div className="card mt-8 p-6 sm:p-7" data-testid="admin-pricing-panel">
      <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-body"><DollarSign size={18} className="text-blue-text" /> Subscription prices</h2>
      <p className="mt-1 text-sm text-muted">Edit what customers see on the pricing cards. The savings badge recalculates automatically.</p>
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {rows.map((r) => <PlanRow key={r.id} row={r} value={pricing[r.id]} onChange={(v) => setPricing({ ...pricing, [r.id]: v })} />)}
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button type="button" onClick={save} disabled={saving} className="btn btn-primary !px-5 !py-2.5 text-sm" data-testid="pricing-save-button">
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <CheckCircle2 size={14} /> : <Save size={14} />} {saved ? "Saved" : "Save prices"}
        </button>
        {error && <p className="text-xs text-red-400" data-testid="pricing-error">{error}</p>}
      </div>
    </div>
  );
};
